import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import styles from './MediaManager.module.css';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button/Button';
import { useApi } from '../../../hooks/useApi';
import { MediaAsset } from '../../../models/media';

interface MediaManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (media: MediaAsset) => void;
  selectedMediaId?: string;
  title?: string;
  allowUpload?: boolean;
  mediaType?: 'image' | 'video';
}

export const MediaManager: React.FC<MediaManagerProps> = observer(
  ({
    isOpen,
    onClose,
    onSelect,
    selectedMediaId,
    title = 'Выберите изображение',
    allowUpload = true,
    mediaType = 'image',
  }) => {
    const api = useApi();
    const [mediaFiles, setMediaFiles] = useState<MediaAsset[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [addingVideo, setAddingVideo] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Загрузка медиа-файлов
    const loadMediaFiles = async (
      pageNum: number = 1,
      append: boolean = false,
    ) => {
      setLoading(true);
      try {
        const response = await api.media.getSimpleMediaFiles({
          page: pageNum,
          limit: 20,
          type: mediaType,
        });

        if (response.success && response.body) {
          const newFiles = response.body.assets || [];
          setMediaFiles(append ? [...mediaFiles, ...newFiles] : newFiles);
          setHasMore(response.body.pagination?.has_next || false);
          setPage(pageNum);
        }
      } catch (error) {
        // Ошибка будет показана в UI
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (isOpen) {
        loadMediaFiles(1, false);
      }
    }, [isOpen, mediaType]);

    // Обработка загрузки файла
    const handleFileUpload = async (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      // Проверка типа файла
      if (mediaType === 'image' && !file.type.startsWith('image/')) {
        alert('Пожалуйста, выберите изображение');
        return;
      }

      setUploading(true);
      try {
        const response = await api.media.uploadSimpleFile(file);
        if (
          response.success &&
          response.body &&
          'media_asset' in response.body
        ) {
          const newMedia = response.body.media_asset;
          setMediaFiles([newMedia, ...mediaFiles]);
          // Автоматически выбираем загруженный файл
          onSelect(newMedia);
        }
      } catch (error) {
        alert('Ошибка загрузки файла');
      } finally {
        setUploading(false);
        // Очищаем input для повторной загрузки того же файла
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    // Обработка добавления видео по URL
    const handleVideoUrlSubmit = async (event: React.FormEvent) => {
      event.preventDefault();

      if (!videoUrl.trim()) {
        alert('Пожалуйста, введите URL видео');
        return;
      }

      setAddingVideo(true);
      try {
        const response = await api.media.addVideo({
          video_url: videoUrl.trim(),
        });

        if (response.success && response.body) {
          const newMedia = response.body.media_asset;
          setMediaFiles([newMedia, ...mediaFiles]);
          setVideoUrl('');
          // Автоматически выбираем добавленное видео
          onSelect(newMedia);
        }
      } catch (error) {
        alert('Ошибка добавления видео. Проверьте URL.');
      } finally {
        setAddingVideo(false);
      }
    };

    // Загрузка следующей страницы
    const loadMore = () => {
      if (!loading && hasMore) {
        loadMediaFiles(page + 1, true);
      }
    };

    const handleMediaSelect = (media: MediaAsset) => {
      onSelect(media);
      onClose();
    };

    const handleDeleteMedia = async (
      mediaId: string,
      event: React.MouseEvent,
    ) => {
      event.stopPropagation(); // Предотвращаем выбор медиа при клике на удаление

      if (!window.confirm('Вы уверены, что хотите удалить это изображение?')) {
        return;
      }

      setDeleting(mediaId);
      try {
        const response = await api.media.delete(mediaId);
        if (response.success) {
          // Удаляем из локального состояния
          setMediaFiles(mediaFiles.filter((media) => media.id !== mediaId));
        } else {
          alert('Ошибка удаления изображения');
        }
      } catch (error) {
        alert('Ошибка удаления изображения');
      } finally {
        setDeleting(null);
      }
    };

    const getImageUrl = (media: MediaAsset): string => {
      // Для изображений используем variants
      if (
        media.type === 'image' &&
        media.variants &&
        'sources' in media.variants
      ) {
        const sources = media.variants.sources;
        if (sources.webp && sources.webp.length > 0) {
          // Ищем наиболее подходящий размер (около 300px)
          const suitableSize =
            sources.webp.find((s) => s.w >= 300) || sources.webp[0];
          return suitableSize.url;
        }
        // Fallback на original
        if (media.variants.meta?.original?.url) {
          return media.variants.meta.original.url;
        }
      }
      return media.original_url || '';
    };

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) {
        return '0 Bytes';
      }
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileName = (media: MediaAsset): string => {
      // Извлекаем имя файла из URL или используем ID
      const url = media.original_url;
      if (url) {
        const parts = url.split('/');
        const fileName = parts[parts.length - 1];
        if (fileName && fileName.includes('.')) {
          return fileName;
        }
      }
      return `media-${media.id}`;
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose} title={title}>
        <div className={styles.mediaManager}>
          {allowUpload && (
            <div className={styles.uploadSection}>
              {mediaType === 'video' ? (
                <div className={styles.videoUploadOptions}>
                  <form
                    onSubmit={handleVideoUrlSubmit}
                    className={styles.videoUrlForm}
                  >
                    <input
                      type="url"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      placeholder="Введите URL видео (YouTube, Accelsite)"
                      className={styles.videoUrlInput}
                      disabled={addingVideo}
                    />
                    <Button
                      type="submit"
                      appearance="auth"
                      disabled={addingVideo || !videoUrl.trim()}
                      className={styles.addVideoButton}
                    >
                      {addingVideo ? 'Добавление...' : 'Добавить видео'}
                    </Button>
                  </form>
                  <div className={styles.uploadDivider}>или</div>
                  <Button
                    type="button"
                    appearance="link"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className={styles.uploadButton}
                  >
                    {uploading ? 'Загрузка...' : 'Загрузить видео файл'}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  appearance="auth"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className={styles.uploadButton}
                >
                  {uploading ? 'Загрузка...' : 'Загрузить новое изображение'}
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={mediaType === 'image' ? 'image/*' : 'video/*'}
                onChange={handleFileUpload}
                className={styles.hiddenInput}
                disabled={uploading}
              />
            </div>
          )}

          <div className={styles.mediaGrid}>
            {mediaFiles.map((media) => (
              <div
                key={media.id}
                className={`${styles.mediaItem} ${
                  selectedMediaId === media.id ? styles.selected : ''
                }`}
                onClick={() => handleMediaSelect(media)}
              >
                <div className={styles.mediaPreview}>
                  {media.type === 'image' ? (
                    <img
                      src={getImageUrl(media)}
                      alt={media.alt || getFileName(media)}
                      className={styles.previewImage}
                    />
                  ) : (
                    <div className={styles.videoPreview}>
                      <span>🎥</span>
                      <span>Видео</span>
                    </div>
                  )}
                  {/* Кнопка удаления */}
                  <button
                    className={styles.deleteButton}
                    onClick={(e) => handleDeleteMedia(media.id, e)}
                    disabled={deleting === media.id}
                    title="Удалить изображение"
                  >
                    {deleting === media.id ? '⏳' : '🗑️'}
                  </button>
                </div>
                <div className={styles.mediaInfo}>
                  <div className={styles.mediaName} title={getFileName(media)}>
                    {getFileName(media)}
                  </div>
                  <div className={styles.mediaSize}>
                    {formatFileSize(media.size_bytes || 0)}
                  </div>
                </div>
                {selectedMediaId === media.id && (
                  <div className={styles.selectedBadge}>✓</div>
                )}
              </div>
            ))}
          </div>

          {loading && (
            <div className={styles.loading}>Загрузка изображений...</div>
          )}

          {hasMore && !loading && (
            <div className={styles.loadMoreSection}>
              <Button
                type="button"
                appearance="link"
                onClick={loadMore}
                className={styles.loadMoreButton}
              >
                Загрузить еще
              </Button>
            </div>
          )}

          {!loading && mediaFiles.length === 0 && (
            <div className={styles.emptyState}>
              <p>Изображения не найдены</p>
              {allowUpload && (
                <p>Загрузите первое изображение, нажав кнопку выше</p>
              )}
            </div>
          )}
        </div>
      </Modal>
    );
  },
);

export default MediaManager;
